import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, Avatar, Descriptions, Table, Tag, Button, Breadcrumb, Row, Col, Space, Typography } from 'antd';
import { ArrowLeftOutlined, UserOutlined } from '@ant-design/icons';
import { RiMailLine, RiFacebookCircleFill, RiPhoneLine, RiAppleFill, RiMessage3Line } from 'react-icons/ri';
import dayjs from 'dayjs';
import { userService } from '../../../services/cms/userServiceRequest';

const { Title, Text } = Typography;

const ensureCdnUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const baseDomain = 'https://cdn.lichviet.org';
  const path = url.startsWith('/') ? url : `/${url}`;
  return baseDomain + path;
};

const getSignalIcon = (type) => {
  switch (type) {
    case 1:
      return <RiMailLine className="text-red-500 text-xl" />;
    case 2:
      return <RiFacebookCircleFill className="text-blue-600 text-xl" />;
    case 3:
      return <RiPhoneLine className="text-green-500 text-xl" />;
    case 4:
      return <RiAppleFill className="text-black text-xl" />;
    case 5:
      return <RiMessage3Line className="text-blue-400 text-xl" />;
    default:
      return <UserOutlined className="text-gray-400 text-xl" />;
  }
};

const getSignalTypeName = (type) => {
  switch (type) {
    case 1: return 'Email';
    case 2: return 'Facebook';
    case 3: return 'Số điện thoại';
    case 4: return 'Apple';
    case 5: return 'Zalo';
    default: return 'Khác';
  }
};

const transactionColumns = [
  {
    title: 'ID Giao dịch',
    dataIndex: 'transaction_id',
    key: 'transaction_id',
    render: (t) => <Text strong>{t || '-'}</Text>,
  },
  {
    title: 'Hình thức',
    dataIndex: 'payment_channel',
    key: 'payment_channel',
  },
  {
    title: 'Thời gian',
    dataIndex: 'transaction_time',
    key: 'transaction_time',
  },
  {
    title: 'Giá trị',
    key: 'value',
    render: (_, r) => {
      const total = (r.transaction_value || 0) * (r.transaction_amount || 1);
      return `${total.toLocaleString()} ${r.currency || 'VND'}`;
    },
  },
  {
    title: 'Số lượng',
    dataIndex: 'transaction_amount',
    key: 'transaction_amount',
    align: 'center',
  },
  {
    title: 'Thể loại',
    dataIndex: 'typeName',
    key: 'typeName',
    render: (t) => t ? <Tag color="blue">{t}</Tag> : '-',
  },
];

const UserDetail = () => {
  const { id } = useParams();

  const { data: detailData, isLoading } = useQuery({
    queryKey: ['userDetail', id],
    queryFn: () => userService.getOne({ id }),
  });

  if (isLoading) {
    return <Card loading={true} className="m-5" />;
  }

  const user = detailData?.user || {};
  const signals = detailData?.signals || [];
  const transactions = detailData?.transactions || [];
  const premiums = detailData?.premiums || [];

  const premiumText = premiums.length
    ? premiums.map((p) => `${p.type_name || `Nhóm ${p.premium_groups}`} — hết hạn ${p.end_time || '-'}`).join('; ')
    : 'Không có';

  const basicInfoItems = [
    { label: 'Tài khoản', children: user.username || '-' },
    { label: 'Email', children: user.email || '-' },
    { label: 'Số điện thoại', children: user.phone || '-' },
    { label: 'SĐT liên hệ', children: user.phone_contact || '-' },
    { label: 'Tổng sự kiện', children: detailData?.total_event ?? 0 },
    { label: 'Gói đang có', children: premiumText, span: 2 },
    { label: 'Ngày sinh', children: user.birthday ? dayjs(user.birthday).format('DD/MM/YYYY') : '-' },
    { label: 'Giới tính', children: user.gender || '-' },
    { label: 'Địa chỉ', children: user.address || '-' },
    { label: 'Số lần đổi ngày sinh còn lại', children: user.birthday_change_time_left },
    { label: 'Thời hạn bỏ QC', children: user.ignore_ads_to_date ? dayjs(user.ignore_ads_to_date).format('DD/MM/YYYY HH:mm') : 'Chưa đăng ký' },
    { label: 'Ngày đăng ký', children: user.create_time ? dayjs(user.create_time).format('DD/MM/YYYY HH:mm') : '-' },
    { label: 'Thiết bị đăng ký', children: user.device_info || 'Không rõ' },
    { label: 'HĐH thiết bị', children: user.os_info || 'Không rõ' },
    { label: 'Mô tả/Về bản thân', children: user.about || '-', span: 2 },
  ].map(item => ({ ...item, labelStyle: { fontWeight: '600', width: 200 } }));

  return (
    <div className="m-5">
      {/* Navigation Header */}
      <div className="flex justify-between items-center mb-6">
        <Space direction="vertical" size={2}>
          <Breadcrumb
            items={[
              { title: <Link to="/cms/quan-ly-user">Quản lý user</Link> },
              { title: 'Chi tiết tài khoản' },
            ]}
          />
          <Title level={3} className="!mb-0 mt-1">
            Chi tiết tài khoản người dùng
          </Title>
        </Space>
        <Link to="/cms/quan-ly-user">
          <Button icon={<ArrowLeftOutlined />}>Quay lại danh sách</Button>
        </Link>
      </div>

      <Row gutter={[20, 20]}>
        {/* Profile Card & Info Description */}
        <Col xs={24} lg={18}>
          <Card className="shadow-sm rounded-xl">
            <div className="flex items-center gap-5 border-b border-gray-100 pb-5 mb-5">
              <Avatar
                src={ensureCdnUrl(user.avatar)}
                icon={<UserOutlined />}
                size={80}
                className="border-2 border-blue-500 shadow-sm"
              />
              <div>
                <Title level={4} className="!mb-1">
                  {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username}
                </Title>
                <Space>
                  <Tag color="purple">ID: {user.id}</Tag>
                  {user.status === 1 ? (
                    <Tag color="green">DUYỆT</Tag>
                  ) : user.status === 0 ? (
                    <Tag color="blue">ĐỢI DUYỆT</Tag>
                  ) : (
                    <Tag color="red">KHÓA</Tag>
                  )}
                </Space>
              </div>
            </div>
            <Descriptions column={2} bordered items={basicInfoItems} />
          </Card>
        </Col>

        {/* Login Signals Panel */}
        <Col xs={24} lg={6}>
          <Card title="Phương thức đăng nhập" className="shadow-sm rounded-xl h-full">
            <div className="flex flex-col gap-4">
              {signals.length === 0 ? (
                <Text type="secondary">Chưa liên kết phương thức đăng nhập nào.</Text>
              ) : (
                signals.map((signal, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                    <div className="p-2 bg-white rounded-md shadow-sm flex items-center justify-center">
                      {getSignalIcon(signal.type)}
                    </div>
                    <div className="overflow-hidden">
                      <div className="font-semibold text-sm text-gray-700">{getSignalTypeName(signal.type)}</div>
                      <div className="text-xs text-gray-500 truncate" title={signal.signal}>
                        {signal.signal || 'Đã liên kết'}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {user.password && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-white rounded-md shadow-sm flex items-center justify-center">
                    <UserOutlined className="text-blue-500 text-xl" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-gray-700">Mật khẩu CMS</div>
                    <div className="text-xs text-gray-500">Đã thiết lập mật khẩu</div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* Purchase Transaction History */}
        <Col xs={24}>
          <Card title={`Lịch sử giao dịch (${transactions.length})`} className="shadow-sm rounded-xl">
            <Table
              columns={transactionColumns}
              dataSource={transactions.map((t, idx) => ({ ...t, key: t.id || idx }))}
              pagination={{ pageSize: 10 }}
              bordered
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserDetail;
